import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { checkEmail, checkText } from '../../Utils/Validator';
import { searchEmailService } from '../../Api/ContactApi';
import { Ripple } from 'primereact/ripple';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

interface EmailSearchDialogProps {
    visible: boolean;
    contactSubtype: string;
    onHide: () => void;
    onEmailConfirm: (email: string) => void; // callback when email is accepted
}

const EmailSearchDialog: React.FC<EmailSearchDialogProps> = ({
    visible,
    contactSubtype,
    onHide,
    onEmailConfirm,
}) => {
    const [email, setEmail] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [allowEmail, setAllowEmail] = useState<boolean | null>(null);
    const [isAlreadyUser, setIsAlreadyUser] = useState<boolean | null>(null);
    const [existUserRole, setExistUserRole] = useState<string | null>(null);
    const [emailSearchResponseMessage, setEmailSearchResponseMessage] = useState<string>('');

    const handleSearchEmail = async () => {
        if (checkSearchEmailCredentials()) {
            setLoading(true);
            const response = await searchEmailService(email, contactSubtype);
            setLoading(false);

            if (response.status) {
                const _isExistsEmail = response.data['allow_contact']
                const _isAlreadyUser = response.data['already_user']
                const _existUserRole = response.data['user_role_name']
                const _emailSearchResponseMessage = response.data['message']
                setAllowEmail(_isExistsEmail);
                setIsAlreadyUser(_isAlreadyUser);
                setExistUserRole(_existUserRole);
                setEmailSearchResponseMessage(_emailSearchResponseMessage);
            } else {
                setAllowEmail(false);
                setIsAlreadyUser(false);
                setExistUserRole(null);
                setEmailSearchResponseMessage('');
            }
        }
    }

    function checkSearchEmailCredentials(): boolean {
        const isSearchEmailValid = checkText(email, true, setErrorEmail) && checkEmail(email, false, setErrorEmail);

        return isSearchEmailValid;
    }

    const handleUseThisEmail = () => {
        onEmailConfirm(email);
        resetDialog();
    };

    const handleOnHide = () => {
        onHide();
        setEmail('');
        setErrorEmail('');
        setAllowEmail(null);
        setEmailSearchResponseMessage('');
        setLoading(false);
    };

    const resetDialog = () => {
        setEmail('');
        setErrorEmail('');
        setAllowEmail(null);
        setEmailSearchResponseMessage('');
        setLoading(false);
        onHide();
    };

    const addSearchEmailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Search email
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleOnHide}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={addSearchEmailModalHeader}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={resetDialog}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                    <div className="row">
                        <div className="col-12">
                            <div className="page_form_group">
                                <Message
                                    severity='info'
                                    className="message_text w-100"
                                    icon="bi bi-info-circle"
                                    style={{ border: '1px solid', borderRadius: '8px' }}
                                    text={`Please enter a valid email address. This email will be used to add details.`}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="email" className="custom_form_label is_required">Email</label>
                                <div className="input_flex_group">
                                    <InputText
                                        id="email"
                                        value={email}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setEmail(e.target.value);
                                            setAllowEmail(null);
                                        }}
                                        className="custom_form_input"
                                        placeholder="Email"
                                        autoComplete='off'
                                        type='search'
                                    />

                                    <Button
                                        loading={loading}
                                        icon="bi bi-search"
                                        className='search_input_btn'
                                        onClick={handleSearchEmail}
                                    />
                                </div>

                                {errorEmail && (
                                    <small className="form_error_msg">
                                        {errorEmail}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-12">
                            {allowEmail != null && email && (
                                <React.Fragment>
                                    {isAlreadyUser && (
                                        <Message
                                            severity='info'
                                            className="message_text w-100 mb-3"
                                            icon="bi bi-info-circle"
                                            style={{ border: '1px solid', borderRadius: '8px' }}
                                            text={() => {
                                                return (
                                                    <span>This user has already registered/subscribed as <b>{existUserRole}</b> in <b>Traqdo</b>.</span>
                                                );
                                            }}
                                        />
                                    )}

                                    {allowEmail ? (
                                        <React.Fragment>
                                            <Message
                                                severity='success'
                                                className="message_text w-100"
                                                icon="bi bi-check-circle"
                                                style={{ border: '1px solid', borderRadius: '8px' }}
                                                text={`This email address is not in use. You can use this email.`}
                                            />
                                            <hr className='my-4' />
                                            <Button
                                                label='Use this email'
                                                icon="bi bi-check-circle"
                                                onClick={handleUseThisEmail}
                                                className='float-end'
                                            />
                                        </React.Fragment>
                                    ) : (
                                        <Message
                                            severity='error'
                                            className="message_text w-100"
                                            icon="bi bi-exclamation-circle"
                                            style={{ border: '1px solid', borderRadius: '8px' }}
                                            text={`This email address already exists. Please use a different email.`}
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default EmailSearchDialog;
